// components/Molecules/IconBullet/IconBullet.js
import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../../Atoms/Icon';
import { RawHtml } from '../../Utilities';

/**
 * IconBullet - Icon + Heading + Description unit
 * 
 * A placement-agnostic list item combining icon with text.
 * Works in vertical lists, horizontal scrolls, grids.
 * 
 * Uses design system primitives:
 * - Block frame for structure
 * - Cluster for horizontal layout (left alignment)
 * - Stack for vertical layout (center alignment)
 * - Typography roles for text hierarchy
 * 
 * Semantic usage (recommended):
 *   <IconBullet icon="TbCheck" heading="Fast" description="..." />
 *   <IconBullet icon="TbCheck" heading="Fast" description="..." usage="compact" />
 *   <IconBullet icon="TbCheck" heading="Fast" description="..." usage="centered" />
 * 
 * Manual usage (edge cases):
 *   <IconBullet icon="TbCheck" heading="Fast" description="..." alignment="center" spacing="loose" />
 */

// Semantic usage presets
const USAGE_PRESETS = {
  default:  { alignment: 'left',   spacing: 'normal' },
  compact:  { alignment: 'left',   spacing: 'tight' },
  centered: { alignment: 'center', spacing: 'normal' },
};


const IconBullet = ({
  icon,
  headingLabel,
  heading,
  description,
  usage,
  alignment = 'left',
  spacing = 'normal',
  className = '',
}) => {
  const finalAlignment = usage && USAGE_PRESETS[usage]
    ? USAGE_PRESETS[usage].alignment
    : alignment;

  const finalSpacing = usage && USAGE_PRESETS[usage]
    ? USAGE_PRESETS[usage].spacing
    : spacing;

  const blockAlignClass = finalAlignment === 'center' ? 'block-align-center' : '';

  const wrapperClasses = [
    'block surface surface-outlined surface-pad-normal bg-neutral-100 text-left h-full flex-col',
    blockAlignClass,
    className,
  ].filter(Boolean).join(' ');

  const iconName =
    typeof icon === 'object'
      ? (icon.iconName || icon.icon_name || icon.name)
      : icon;

  const iconLibrary =
    typeof icon === 'object' ? icon.library : undefined;

  if (!heading && !headingLabel && !description) return null;

  const hasIcon = !!iconName;

  return (
    <div className={wrapperClasses}>
      <div className={`block__body stack stack-${finalSpacing}`}>
        {hasIcon && (
          <div className="icon-bullet__iconWrap" aria-hidden="true">
            <Icon
              name={iconName}
              library={iconLibrary}
              usage="bullet"
              className="icon-sm"
            />
          </div>
        )}
        <div className="stack stack-tight">
          {headingLabel && <div className="text-label">{headingLabel}</div>}
          {heading && <p className="text-item-title">{heading}</p>}
          {description && (
            <div className="text-body-sm text-muted">
              <RawHtml html={description} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

IconBullet.propTypes = {
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      iconName: PropTypes.string,
      icon_name: PropTypes.string,
      name: PropTypes.string,
      library: PropTypes.string,
    }),
  ]),
  heading: PropTypes.string,
  headingLabel: PropTypes.string,
  description: PropTypes.string,
  usage: PropTypes.oneOf(['default', 'compact', 'centered']),
  alignment: PropTypes.oneOf(['left', 'center']),
  spacing: PropTypes.oneOf(['tight', 'normal', 'loose']),
  className: PropTypes.string,
};

export default IconBullet;